import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaFileCsv, FaPlus, FaTrashAlt, FaUsersCog } from 'react-icons/fa'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface AuditCase {
    id: number
    case_reference: string
    funder_client_id: number
    client_name: string | null
    client_email: string | null
    introducer: string | null
    added_by: string | null
    added_at: string
}

interface AuditorUser {
    username: string
    email: string
    name: string
    organisation: string
    enabled: boolean
    status: string
    lastLogin: string | null
}

interface ImportReport {
    total: number
    matched: {
        case_reference: string
        funder_client_id: number
        client_name: string | null
        client_email: string | null
        introducer: string | null
    }[]
    unmatched: string[]
    alreadyAdded: string[]
    inserted: number
}

const AUDIT_ORG = 'Insurance Audit'

const formatDate = (value: string | null) => {
    if (!value) return '—'
    try {
        return new Date(value).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
        return value
    }
}

export const InsuranceAuditAdminView = () => {
    const navigate = useNavigate()
    const [cases, setCases] = useState<AuditCase[]>([])
    const [auditors, setAuditors] = useState<AuditorUser[]>([])
    const [caseFilter, setCaseFilter] = useState('')
    const [newCaseRef, setNewCaseRef] = useState('')
    const [isAdding, setIsAdding] = useState(false)
    // CSV import: parsed preview report awaiting confirmation. The base64
    // payload is kept alongside so "confirm" re-sends the exact same file.
    const [importReport, setImportReport] = useState<ImportReport | null>(null)
    const [importFileData, setImportFileData] = useState<string | null>(null)
    const [isImporting, setIsImporting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        loadCases()
        loadAuditors()
    }, [])

    const loadCases = async () => {
        try {
            Loading.circle('Loading audit cases...')
            const result = await api({ url: '/insurance-audit-cases-list', data: {} })
            if (result.data.success) {
                setCases(result.data.cases || [])
            } else {
                Report.failure('Error', 'Failed to load audit cases.', 'Okay')
            }
        } catch (error) {
            console.error('Error loading audit cases:', error)
            Report.failure('Error', 'Failed to load audit cases.', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    const loadAuditors = async () => {
        try {
            const result = await api({ url: '/cognito-users-all', data: {} })
            if (result.data.success) {
                const users: AuditorUser[] = (result.data.users || []).filter(
                    (user: AuditorUser) => user.organisation === AUDIT_ORG,
                )
                setAuditors(users)
            }
        } catch (error) {
            // Non-fatal — the case whitelist is the primary content here.
            console.error('Error loading auditor accounts:', error)
        }
    }

    const handleAddCase = async () => {
        const caseReference = newCaseRef.trim()
        if (!caseReference) return
        try {
            setIsAdding(true)
            Loading.circle('Resolving case reference...')
            const result = await api({ url: '/insurance-audit-cases-add', data: { caseReference } })
            Loading.remove()
            if (result.data.success) {
                setNewCaseRef('')
                Report.success(
                    'Case Added',
                    `"${caseReference}" resolved to ${result.data.resolved?.client_name || 'a funded client'} and is now visible to insurance auditors.`,
                    'Okay',
                )
                loadCases()
            } else {
                Report.failure('Error', result.data.message || 'Failed to add the case reference.', 'Okay')
            }
        } catch (error: any) {
            Loading.remove()
            Report.failure(
                'Could Not Add Case',
                error?.response?.data?.message || 'Failed to add the case reference.',
                'Okay',
            )
        } finally {
            setIsAdding(false)
        }
    }

    const handleRemoveCase = (auditCase: AuditCase) => {
        Confirm.show(
            'Remove Case',
            `Remove "${auditCase.case_reference}" from the insurance audit whitelist? Auditors will lose access to this case.`,
            'Remove',
            'Cancel',
            async () => {
                try {
                    Loading.circle('Removing case...')
                    const result = await api({ url: '/insurance-audit-cases-remove', data: { id: auditCase.id } })
                    Loading.remove()
                    if (result.data.success) {
                        setCases((prev) => prev.filter((c) => c.id !== auditCase.id))
                    } else {
                        Report.failure('Error', result.data.message || 'Failed to remove the case.', 'Okay')
                    }
                } catch (error) {
                    Loading.remove()
                    Report.failure('Error', 'Failed to remove the case.', 'Okay')
                }
            },
        )
    }

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = async () => {
            const dataUrl = String(reader.result || '')
            const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
            setImportFileData(base64)
            await runImport(base64, true)
        }
        reader.readAsDataURL(file)
        // Reset so selecting the same file again re-triggers onChange.
        event.target.value = ''
    }

    const runImport = async (fileData: string, preview: boolean) => {
        try {
            setIsImporting(true)
            Loading.circle(preview ? 'Checking CSV against the funding database...' : 'Importing cases...')
            const result = await api({
                url: '/insurance-audit-cases-import-csv',
                data: { fileData, preview },
            })
            Loading.remove()
            if (!result.data.success) {
                Report.failure('Import Failed', result.data.message || 'CSV import failed.', 'Okay')
                return
            }
            if (preview) {
                setImportReport(result.data.report)
            } else {
                setImportReport(null)
                setImportFileData(null)
                Report.success(
                    'Import Complete',
                    `${result.data.report.inserted} case reference(s) added to the insurance audit whitelist.`,
                    'Okay',
                )
                loadCases()
            }
        } catch (error: any) {
            Loading.remove()
            Report.failure(
                'Import Failed',
                error?.response?.data?.message || 'CSV import failed.',
                'Okay',
            )
        } finally {
            setIsImporting(false)
        }
    }

    const filteredCases = useMemo(() => {
        const term = caseFilter.trim().toLowerCase()
        if (!term) return cases
        return cases.filter(
            (c) =>
                c.case_reference.toLowerCase().includes(term) ||
                (c.client_name || '').toLowerCase().includes(term) ||
                (c.client_email || '').toLowerCase().includes(term) ||
                (c.introducer || '').toLowerCase().includes(term),
        )
    }, [cases, caseFilter])

    return (
        <div className={styles.adminView}>
            {/* ── Auditor accounts ─────────────────────────────────────── */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2>
                        <FaUsersCog /> Auditor Accounts
                    </h2>
                    <button className={styles.secondaryButton} onClick={() => navigate('/users')}>
                        Manage in User Management
                    </button>
                </div>
                <p className={styles.sectionHint}>
                    Users with the "{AUDIT_ORG}" organisation can log in and see only the whitelisted audit cases
                    below. Create or assign auditor accounts from the User Management page.
                </p>
                {auditors.length === 0 ? (
                    <div className={styles.emptyState}>No auditor accounts yet.</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Last Login</th>
                            </tr>
                        </thead>
                        <tbody>
                            {auditors.map((user) => (
                                <tr key={user.username}>
                                    <td>{user.name || '—'}</td>
                                    <td>{user.email}</td>
                                    <td>
                                        <span
                                            className={`${styles.statusBadge} ${
                                                user.enabled ? styles.statusActive : styles.statusDisabled
                                            }`}
                                        >
                                            {user.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td>{formatDate(user.lastLogin)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* ── Case whitelist ───────────────────────────────────────── */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2>
                        <FaFileCsv /> Audit Case Whitelist ({cases.length})
                    </h2>
                    <button className={styles.secondaryButton} onClick={() => navigate('/insurance-audit')}>
                        View Audit Page
                    </button>
                </div>
                <p className={styles.sectionHint}>
                    Add case references or claimant IDs (funder client IDs). Values are resolved against the funding
                    database when added — anything that doesn't match a funded claim is rejected. Auditors can view
                    every whitelisted client.
                </p>

                <div className={styles.controls}>
                    <div className={styles.addCaseControl}>
                        <input
                            type="text"
                            placeholder="Add a case reference or claimant ID..."
                            value={newCaseRef}
                            onChange={(e) => setNewCaseRef(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !isAdding && handleAddCase()}
                        />
                        <button
                            className={styles.primaryButton}
                            onClick={handleAddCase}
                            disabled={isAdding || !newCaseRef.trim()}
                        >
                            <FaPlus /> Add Case
                        </button>
                    </div>
                    <div className={styles.uploadControl}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: 'none' }}
                            onChange={handleFileSelected}
                        />
                        <button
                            className={styles.secondaryButton}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isImporting}
                        >
                            <FaFileCsv /> Upload CSV
                        </button>
                    </div>
                    <input
                        className={styles.filterInput}
                        type="text"
                        placeholder="Filter cases..."
                        value={caseFilter}
                        onChange={(e) => setCaseFilter(e.target.value)}
                    />
                </div>

                {filteredCases.length === 0 ? (
                    <div className={styles.emptyState}>
                        {cases.length === 0
                            ? 'No cases whitelisted yet. Add a case reference or upload a CSV to get started.'
                            : 'No cases match the filter.'}
                    </div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Reference / Claimant ID</th>
                                <th>Client</th>
                                <th>Email</th>
                                <th>Introducer</th>
                                <th>Added By</th>
                                <th>Added</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCases.map((auditCase) => (
                                <tr key={auditCase.id}>
                                    <td className={styles.monospace}>{auditCase.case_reference}</td>
                                    <td>{auditCase.client_name || '—'}</td>
                                    <td>{auditCase.client_email || '—'}</td>
                                    <td>{auditCase.introducer || '—'}</td>
                                    <td>{auditCase.added_by || '—'}</td>
                                    <td>{formatDate(auditCase.added_at)}</td>
                                    <td>
                                        <button
                                            className={styles.removeButton}
                                            title="Remove from whitelist"
                                            onClick={() => handleRemoveCase(auditCase)}
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* ── CSV import preview modal ─────────────────────────────── */}
            {importReport && (
                <div className={styles.modalOverlay} onClick={() => !isImporting && setImportReport(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h3>CSV Import Preview</h3>
                        <p className={styles.sectionHint}>
                            One case reference or claimant ID per row (first column is used).
                        </p>
                        <div className={styles.reportSummary}>
                            <div className={styles.reportStat}>
                                <span className={styles.reportNumber}>{importReport.total}</span>
                                <span>references in file</span>
                            </div>
                            <div className={`${styles.reportStat} ${styles.reportMatched}`}>
                                <span className={styles.reportNumber}>{importReport.matched.length}</span>
                                <span>matched</span>
                            </div>
                            <div className={`${styles.reportStat} ${styles.reportUnmatched}`}>
                                <span className={styles.reportNumber}>{importReport.unmatched.length}</span>
                                <span>not found</span>
                            </div>
                            <div className={styles.reportStat}>
                                <span className={styles.reportNumber}>{importReport.alreadyAdded.length}</span>
                                <span>already whitelisted</span>
                            </div>
                        </div>

                        {importReport.matched.length > 0 && (
                            <div className={styles.reportList}>
                                <h4>Will be added</h4>
                                <ul>
                                    {importReport.matched.map((m) => (
                                        <li key={m.case_reference}>
                                            <span className={styles.monospace}>{m.case_reference}</span>
                                            {' — '}
                                            {m.client_name || 'Unknown client'}
                                            {m.introducer ? ` (${m.introducer})` : ''}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {importReport.unmatched.length > 0 && (
                            <div className={`${styles.reportList} ${styles.reportListUnmatched}`}>
                                <h4>Not found in the funding database (will be skipped)</h4>
                                <ul>
                                    {importReport.unmatched.map((ref) => (
                                        <li key={ref} className={styles.monospace}>
                                            {ref}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className={styles.modalActions}>
                            <button
                                className={styles.secondaryButton}
                                disabled={isImporting}
                                onClick={() => {
                                    setImportReport(null)
                                    setImportFileData(null)
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.primaryButton}
                                disabled={isImporting || importReport.matched.length === 0 || !importFileData}
                                onClick={() => importFileData && runImport(importFileData, false)}
                            >
                                Import {importReport.matched.length} Case
                                {importReport.matched.length === 1 ? '' : 's'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
